import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getTerm } from "$stores/dictionary";

import TableLoader from "$components/table/table-loader";

import { initiativesApi } from "./api";
import { buildDefaultColumns, getFocusTypeLabels } from "./libs/columns";

export const mapToTableData = (i) => ({
    key: i.id,
    ...i,
    focusTypeLabel: getFocusTypeLabels()[i.focusType] || i.focusType,
});

const applyFilters = (data, filters) => {
    let result = data;
    if (filters.focusTypeLabel?.length) {
        result = result.filter((i) => filters.focusTypeLabel.includes(i.focusType));
    }
    if (filters.status?.length) {
        result = result.filter((i) => filters.status.includes(i.status));
    }
    if (filters.targetDate) {
        const filterDate = new Date(filters.targetDate);
        result = result.filter((i) => i.targetDate && new Date(i.targetDate) >= filterDate);
    }
    if (filters.ownerName?.length) {
        result = result.filter((i) => filters.ownerName.includes(i.ownerName));
    }
    return result;
};

const applySort = (data, sort) => {
    const entries = Object.entries(sort);
    if (!entries.length) return data;
    const [key, dir] = entries[0];
    return [...data].sort((a, b) => {
        const aVal = a[key] ?? "";
        const bVal = b[key] ?? "";
        if (aVal < bVal) return dir === "asc" ? -1 : 1;
        if (aVal > bVal) return dir === "asc" ? 1 : -1;
        return 0;
    });
};

const InitiativeList = ({ onCreateNew }) => {
    const [initiatives, setInitiatives] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filters, setFilters] = useState({});
    const [sort, setSort] = useState({ lastActivityDate: "desc" });
    const navigate = useNavigate();

    useEffect(() => {
        const controller = new AbortController();
        initiativesApi
            .list(controller.signal)
            .then((res) => setInitiatives(res || []))
            .catch((err) => {
                if (err?.name !== "AbortError") console.error(err);
            })
            .finally(() => setLoading(false));
        return () => controller.abort();
    }, []);

    const ownerOptions = useMemo(() => {
        const names = [...new Set(initiatives.map((i) => i.ownerName))].sort();
        return names.map((name) => ({ value: name, text: name }));
    }, [initiatives]);

    const columns = useMemo(() => buildDefaultColumns({ ownerOptions }), [ownerOptions]);

    const tableData = useMemo(() => {
        const mapped = initiatives.map(mapToTableData);
        const filtered = applyFilters(mapped, filters);
        return applySort(filtered, sort);
    }, [initiatives, filters, sort]);

    const isSortAsc = Object.values(sort)[0] === "asc";

    if (!loading && initiatives.length === 0) {
        return (
            <div className="initiatives-list__empty txt -center">
                <h4>No {getTerm("initiatives").toLowerCase()} yet.</h4>
                <p>Create an initiative to start tracking business development opportunities.</p>
                <div className="button-group -center">
                    <button className="button -primary -small" onClick={onCreateNew}>
                        + New {getTerm("initiative")}
                    </button>
                </div>
            </div>
        );
    }

    return (
        <TableLoader
            allowFilter={true}
            columns={columns}
            containerClass="initiative-list"
            data={tableData}
            filters={filters}
            isLoading={loading}
            isSortAsc={isSortAsc}
            onRowClick={(row) => navigate(`/initiatives/${row.key}`)}
            setFilters={setFilters}
            setSort={setSort}
            showPagnitaion={false}
            sort={sort}
        />
    );
};

export default InitiativeList;
